import { useEffect, useState } from 'react';
import SupportDrawer, { recordLastError, type SupportPreset } from './SupportDrawer';
import { currentUser } from '../api/auth';
import { fetchSupportUnread } from '../api/supportTickets';
import type { User } from '../api/types';
import { t } from '../lib/i18n';

/**
 * Persistent bottom-right floating button. Visible on every authenticated
 * page (anything that mounts the SupportButton — App.tsx mounts it once
 * outside the routes so it survives navigation).
 *
 * Login page does NOT mount this (no session yet). The component checks
 * /api/me on mount and self-hides if unauthenticated.
 */
export default function SupportButton() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'help' | 'support'>('help');
  const [preset, setPreset] = useState<SupportPreset | undefined>(undefined);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    let alive = true;
    currentUser()
      .then((u) => { if (alive) setUser(u); })
      .catch(() => { if (alive) setUser(null); });
    return () => { alive = false; };
  }, []);

  // Ticket-board unread badge (JCD-20/JCD-21). One-shot on login, matching
  // "no polling" for the board itself — a stale count just clears next time
  // the button remounts (e.g. after visiting /soporte/tickets, which acks).
  // Best-effort: a failed fetch just means no badge, never a broken button.
  useEffect(() => {
    if (!user) return;
    let alive = true;
    fetchSupportUnread()
      .then((r) => { if (alive) setUnread(r.count); })
      .catch(() => { /* badge is best-effort */ });
    return () => { alive = false; };
  }, [user]);

  // Capture the last JS error so a ticket can include it. window.onerror
  // catches synchronous errors; unhandledrejection covers async promise
  // rejections (common with fetch failures). Both are read-only — we don't
  // suppress anything, just record.
  useEffect(() => {
    const onErr = (e: ErrorEvent) => recordLastError(e.message);
    const onRej = (e: PromiseRejectionEvent) =>
      recordLastError(typeof e.reason === 'string' ? e.reason : String(e.reason));
    window.addEventListener('error', onErr);
    window.addEventListener('unhandledrejection', onRej);
    return () => {
      window.removeEventListener('error', onErr);
      window.removeEventListener('unhandledrejection', onRej);
    };
  }, []);

  // Any page can open the drawer pre-seeded via a window event, e.g. the
  // Reports "Solicitar un reporte" action. detail: { tab?, preset? }.
  useEffect(() => {
    const onOpen = (e: Event) => {
      const d = (e as CustomEvent).detail as { tab?: 'help' | 'support'; preset?: SupportPreset } | undefined;
      setTab(d?.tab ?? 'support');
      setPreset(d?.preset);
      setOpen(true);
    };
    window.addEventListener('pacebi:support-open', onOpen as EventListener);
    return () => window.removeEventListener('pacebi:support-open', onOpen as EventListener);
  }, []);

  if (!user) return null;

  // The count rides in the button's own accessible name rather than a nested
  // aria-label on the pill: a button's aria-label overrides all descendant
  // text/labels for assistive tech, so a separate label on the pill would
  // never actually be announced. The pill itself is aria-hidden — decorative.
  const label = unread > 0
    ? `${t.support.openButton} (${unread} ${t.supportTickets.unreadLabel})`
    : t.support.openButton;

  return (
    <>
      <button
        type="button"
        className="fixed bottom-5 right-5 z-sticky w-12 h-12 rounded-full bg-tremor-brand text-tremor-brand-inverted shadow-lg hover:shadow-xl hover:scale-105 transition-transform flex items-center justify-center text-lg font-semibold"
        onClick={() => { setTab('help'); setPreset(undefined); setOpen(true); }}
        aria-label={label}
        title={label}
      >
        ?
        {unread > 0 && (
          <span
            aria-hidden="true"
            className="absolute -top-1 -right-1 min-w-[1.25rem] h-5 px-1 rounded-full bg-red-600 text-white text-xs flex items-center justify-center"
          >
            {unread}
          </span>
        )}
      </button>
      {open && <SupportDrawer user={user} onClose={() => setOpen(false)} defaultTab={tab} preset={preset} />}
    </>
  );
}
